import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Space, Tabs, Divider, Radio, message } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone, MobileOutlined, FormOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/useAuth';
import { parentAPI } from '../services/api';
import './Login.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface LoginProps {
  redirect?: string;
}

const Login: React.FC<LoginProps> = ({ redirect = '/admin/dashboard' }) => {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("login");
  const [userType, setUserType] = useState<'ADMIN' | 'TEACHER' | 'PARENT'>('TEACHER');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);

  const handleSubmit = async (values: { username: string; password: string }) => {
    setLoginError(null);

    try {
      let success = false;

      // 根据用户选择的类型和输入内容判断登录方式
      // 家长账号是手机号格式（11位数字），可以直接识别
      const isPhoneNumber = /^1[3-9]\d{9}$/.test(values.username);

      if (isPhoneNumber && userType === 'PARENT') {
        // 识别为家长账号，直接家长登录
        success = await login(values.username, values.password, 'PARENT');
        if (success) {
          navigate('/parent/dashboard');
          return;
        }
      } else if (userType === 'TEACHER') {
        // 先尝试教师登录
        success = await login(values.username, values.password, 'TEACHER');
        if (success) {
          navigate('/teacher/dashboard');
          return;
        }

        // 如果教师登录失败，静默尝试管理员登录
        try {
          success = await login(values.username, values.password, 'ADMIN');
          if (success) {
            navigate('/admin/dashboard');
            return;
          }
        } catch (error) {
          // 管理员登录也失败，不显示错误
        }
      } else if (userType === 'PARENT') {
        // 非手机号但选择了家长登录，先尝试家长登录
        success = await login(values.username, values.password, 'PARENT');
        if (success) {
          navigate('/parent/dashboard');
          return;
        }

        // 如果家长登录失败，静默尝试管理员登录
        try {
          success = await login(values.username, values.password, 'ADMIN');
          if (success) {
            navigate('/admin/dashboard');
            return;
          }
        } catch (error) {
          // 管理员登录也失败，不显示错误
        }
      }

      // 所有尝试都失败，显示错误
      if (!success) {
        setLoginError('登录失败，请检查账号和密码');
      }
    } catch (error) {
      console.error('登录过程出错:', error);
      setLoginError('登录过程中发生错误，请稍后再试');
    }
  };

  const handleRegisterSubmit = async (values: { phone: string; password: string; confirmPassword: string; remark: string }) => {
    setRegisterError(null);
    setRegisterLoading(true);
    
    try {
      if (values.password !== values.confirmPassword) {
        setRegisterError('两次输入的密码不一致');
        return;
      }
      
      const data = {
        phone: values.phone,
        password: values.password,
        remark: values.remark
      };
      
      const response = await parentAPI.register(data);
      
      if (response.status === 201) {
        message.success('注册申请已提交，请等待管理员审核');
        setRegisterSuccess(true);
        // 重置表单
        registerForm.resetFields();
      } else {
        setRegisterError('注册申请提交失败，请稍后再试');
      }
    } catch (error: any) {
      console.error('注册申请提交出错:', error);
      let errorMsg = '注册申请提交失败';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      setRegisterError(errorMsg);
    } finally {
      setRegisterLoading(false);
    }
  };

  const goToHomePage = () => {
    navigate('/');
  };

  const handleUserTypeChange = (e: any) => {
    setUserType(e.target.value);
    setLoginError(null);
  };

  const renderLoginForm = () => (
    <Form
      form={loginForm}
      name="login"
      onFinish={handleSubmit}
      autoComplete="off"
      layout="vertical"
      className="login-form"
    >
      <Form.Item className="user-type-selector">
        <Radio.Group 
          value={userType} 
          onChange={handleUserTypeChange}
          buttonStyle="solid"
        >
          <Radio.Button value="TEACHER">教师</Radio.Button>
          <Radio.Button value="PARENT">家长</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="username"
        rules={[{ required: true, message: userType === 'PARENT' ? '请输入手机号' : '请输入用户名' }]}
      >
        <Input 
          prefix={userType === 'PARENT' ? <MobileOutlined className="input-icon" /> : <UserOutlined className="input-icon" />} 
          placeholder={userType === 'PARENT' ? '手机号' : '用户名'} 
          size="large"
          className="login-input"
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[{ required: true, message: '请输入密码' }]}
      >
        <Input.Password 
          prefix={<LockOutlined className="input-icon" />} 
          placeholder="密码" 
          size="large"
          className="login-input"
          iconRender={visible => (
            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
          )}
        />
      </Form.Item>

      {loginError && (
        <div className="login-error">
          {loginError}
        </div>
      )}

      <Form.Item className="login-buttons">
        <div className="button-container">
          <Button
            onClick={goToHomePage}
            className="back-button"
          >
            返回
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            className="submit-button"
          >
            登录
          </Button>
        </div>
      </Form.Item>
    </Form>
  );

  const renderRegisterForm = () => (
    <Form
      form={registerForm}
      name="register"
      onFinish={handleRegisterSubmit}
      autoComplete="off"
      layout="vertical"
      className="register-form"
    >
      {registerSuccess ? (
        <div className="register-success">
          <Text type="success">注册申请已提交成功！请等待管理员审核。</Text>
          <Button 
            type="primary" 
            onClick={() => {
              setRegisterSuccess(false);
              setActiveTab("login");
              setUserType('PARENT');
            }}
            style={{ marginTop: 16 }}
          >
            返回登录
          </Button>
        </div>
      ) : (
        <>
          <Form.Item
            name="phone"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' }
            ]}
          >
            <Input 
              prefix={<MobileOutlined className="input-icon" />} 
              placeholder="手机号" 
              size="large"
              className="register-input"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6个字符' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined className="input-icon" />} 
              placeholder="设置密码" 
              size="large"
              className="register-input"
              iconRender={visible => (
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              )}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined className="input-icon" />} 
              placeholder="确认密码" 
              size="large"
              className="register-input"
              iconRender={visible => (
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              )}
            />
          </Form.Item>

          <Form.Item
            name="remark"
            rules={[
              { required: true, message: '请输入备注说明' },
              { min: 5, message: '备注至少5个字符' }
            ]}
          >
            <TextArea 
              placeholder="请简要说明您的孩子情况，如姓名、年级等" 
              autoSize={{ minRows: 3, maxRows: 5 }}
              className="register-input"
            />
          </Form.Item>

          {registerError && (
            <div className="register-error">
              {registerError}
            </div>
          )}

          <Form.Item className="register-buttons">
            <div className="button-container">
              <Button
                onClick={() => setActiveTab("login")}
                className="back-button"
              >
                返回登录
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={registerLoading}
                className="submit-button"
                icon={<FormOutlined />}
              >
                提交申请
              </Button>
            </div>
          </Form.Item>
        </>
      )}
    </Form>
  );

  return (
    <div className="login-container">
      <Card className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/images/logo.png" alt="学习原力Studyforce" className="logo-image" />
          </div>
        </div>
        
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          className="login-tabs"
          centered
        >
          <TabPane tab="登录" key="login">
            {renderLoginForm()}
          </TabPane>
          <TabPane tab="家长注册" key="register">
            {renderRegisterForm()}
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default Login; 